import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { fmtDateTime } from "../lib";
import { Shell, type ScreenProps } from "./common";

type Row = {
  id: string; name: string; login: string; role: "rep" | "manager";
  active: boolean; lastSeenAt: string | null;
  status: "active" | "invited" | "revoked" | "no-access";
};

const STATUS: Record<Row["status"], { text: string; cls: string }> = {
  active:     { text: "работает",          cls: "g" },
  invited:    { text: "ждёт первого входа", cls: "w" },
  revoked:    { text: "доступ отозван",     cls: "c" },
  "no-access":{ text: "нет доступа",        cls: "n" },
};

export default function Staff({ updateReady, back, state, user }: ScreenProps) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [login, setLogin] = useState("");
  const [role, setRole] = useState<"rep" | "manager">("rep");
  const [days, setDays] = useState(5);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<{ url: string; until: string; who: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api<{ users: Row[] }>("/api/auth/users");
      setRows(res.users);
      setError(null);
    } catch (e: any) {
      setError(e?.status === 0 ? "Нет связи — список сотрудников виден только онлайн" : e?.message);
      setRows([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function invite() {
    if (!name.trim() || !login.trim()) return setError("Заполни имя и логин");
    setBusy(true); setError(null);
    try {
      const res = await api<{ inviteUrl: string; expiresAt: string; user: { name: string } }>(
        "/api/auth/invite",
        { method: "POST", body: { name: name.trim(), login: login.trim(), role, days } }
      );
      setLink({ url: res.inviteUrl, until: res.expiresAt, who: res.user.name });
      setName(""); setLogin(""); setAdding(false);
      await load();
    } catch (e: any) {
      setError(e?.message ?? "Не удалось создать приглашение");
    } finally {
      setBusy(false);
    }
  }

  async function reissue(row: Row) {
    setBusy(true); setError(null);
    try {
      const res = await api<{ inviteUrl: string; expiresAt: string; user: { name: string } }>(
        "/api/auth/invite",
        // reset: сотрудник уже заведён, и пароль у него, скорее всего, есть.
        { method: "POST", body: { name: row.name, login: row.login, role: row.role, days, reset: true } }
      );
      setLink({ url: res.inviteUrl, until: res.expiresAt, who: row.name });
      await load();
    } catch (e: any) {
      setError(e?.message ?? "Не удалось выписать ссылку");
    } finally {
      setBusy(false);
    }
  }

  async function toggle(row: Row) {
    setBusy(true); setError(null);
    try {
      await api(`/api/auth/${row.active ? "revoke" : "restore"}/${row.id}`, { method: "POST" });
      await load();
    } catch (e: any) {
      setError(e?.message ?? "Не получилось");
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError("Скопировать не вышло — выдели ссылку вручную");
    }
  }

  return (
    <Shell
      title="Сотрудники"
      onBack={back}
      state={state} updateReady={updateReady}
      footer={
        !adding ? (
          <button className="btn primary" onClick={() => { setAdding(true); setLink(null); }}>
            + Добавить сотрудника
          </button>
        ) : (
          <button className="btn ghost" onClick={() => setAdding(false)}>Отмена</button>
        )
      }
    >
      {error && <div className="err">{error}</div>}

      {link && (
        <div className="block" style={{ borderColor: "var(--accent)" }}>
          <div className="blockhead">
            <span className="blocktitle">Ссылка для «{link.who}»</span>
          </div>
          <div style={{
            background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 10,
            padding: "12px 14px", fontSize: 14, wordBreak: "break-all", marginBottom: 12,
          }}>
            {link.url}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn tiny primary" onClick={copy}>
              {copied ? "Скопировано" : "Скопировать"}
            </button>
            <button className="btn tiny" onClick={() => setLink(null)}>Скрыть</button>
          </div>
          <div className="hint" style={{ marginTop: 10 }}>
            Действует до {fmtDateTime(link.until)}, срабатывает один раз.
            Отправь её сотруднику — пароль он придумает сам.
          </div>
        </div>
      )}

      {adding && (
        <div className="block">
          <div className="blockhead"><span className="blocktitle">Новый сотрудник</span></div>
          <div className="field">
            <label className="label" htmlFor="nm">Имя и фамилия <span className="req">*</span></label>
            <input id="nm" className="inp" value={name} placeholder="Игорь Савченко"
              onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label className="label" htmlFor="lg">Логин <span className="req">*</span></label>
            <input id="lg" className="inp" value={login} placeholder="i.savchenko"
              autoCapitalize="none" autoCorrect="off"
              onChange={(e) => setLogin(e.target.value)} />
            <div className="hint">Латиницей, без пробелов. Можно точку и дефис</div>
          </div>
          <div className="row2">
            <div className="field">
              <label className="label" htmlFor="rl">Роль</label>
              <select id="rl" className="inp" value={role}
                onChange={(e) => setRole(e.target.value as "rep" | "manager")}>
                <option value="rep">Медпред — только свои визиты</option>
                <option value="manager">Руководитель — видит всё</option>
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="dy">Ссылка живёт</label>
              <select id="dy" className="inp" value={days}
                onChange={(e) => setDays(Number(e.target.value))}>
                <option value={1}>сутки</option>
                <option value={3}>3 дня</option>
                <option value={5}>5 дней</option>
                <option value={14}>2 недели</option>
              </select>
            </div>
          </div>
          <button className="btn primary" onClick={invite} disabled={busy}>
            {busy ? "Создаём…" : "Создать ссылку"}
          </button>
        </div>
      )}

      {rows === null && <div className="empty">Загрузка…</div>}

      <div className="list">
        {rows?.map((r) => {
          const s = STATUS[r.status];
          return (
            <div className="item" key={r.id} style={{ cursor: "default", alignItems: "flex-start" }}>
              <span className="main">
                <span className="name" style={{ whiteSpace: "normal" }}>
                  {r.name}
                  {r.role === "manager" && <span className="hint"> · руководитель</span>}
                  {r.id === user.id && <span className="hint"> · это вы</span>}
                </span>
                <span className="meta">
                  {r.login}
                  {r.lastSeenAt ? " · был " + fmtDateTime(r.lastSeenAt) : " · ещё не заходил"}
                </span>
                <span style={{ display: "flex", gap: 8, marginTop: 9, flexWrap: "wrap" }}>
                  {r.id !== user.id && (
                    <>
                      <button className="btn tiny" onClick={() => reissue(r)} disabled={busy}>
                        {r.status === "active" ? "Сбросить пароль" : "Выписать ссылку"}
                      </button>
                      <button className="btn tiny" onClick={() => toggle(r)} disabled={busy}>
                        {r.active ? "Отозвать доступ" : "Вернуть доступ"}
                      </button>
                    </>
                  )}
                </span>
              </span>
              <span className={"pill " + s.cls}>{s.text}</span>
            </div>
          );
        })}
      </div>

      <div className="note">
        <b>Как это работает.</b> Вы создаёте сотруднику ссылку и отправляете её. Он переходит,
        придумывает пароль и сразу попадает внутрь. Пароль знает только он — вы его не видите
        и не пересылаете.
      </div>
      <div className="hint">
        «Отозвать доступ» — на случай потерянного планшета: при следующем выходе в сеть
        приложение разлогинится и сотрёт с планшета все данные.
        Памятка по установке для сотрудника: <b>crm.natusana.by/pamyatka</b>
      </div>
    </Shell>
  );
}
